import { ballColor, ballColorDark, ringWidth } from '@photo-merge/shared';
import type { BallArt } from './art';
import type { Ball, GameEngine } from './engine';
import { DROP_Y, FILL_LINE_Y } from './constants';

/**
 * Canvas renderer. Matter's built-in renderer is a debug tool and can't draw a photo
 * clipped into a circle, so the game draws itself.
 *
 * Everything here works in world units and relies on a single transform set up in
 * `draw`, so no drawing code has to know about device pixel ratio or letterboxing.
 */
/** Board colours, pulled from CSS custom properties so the canvas follows the theme. */
interface BoardTheme {
  wall: string;
  line: string;
  lineDanger: string;
  guide: string;
  ballShadow: string;
}

const FALLBACK_THEME: BoardTheme = {
  wall: 'rgba(180,120,95,0.28)',
  line: 'rgba(150,110,95,0.3)',
  lineDanger: 'rgba(224,82,82,0.85)',
  guide: 'rgba(150,110,95,0.3)',
  ballShadow: 'rgba(120,70,50,0.16)',
};

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private scale = 1;
  private theme: BoardTheme = FALLBACK_THEME;

  constructor(
    private canvas: HTMLCanvasElement,
    private engine: GameEngine,
    private art: BallArt,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.readTheme();
  }

  setArt(art: BallArt): void {
    this.art = art;
  }

  /**
   * Resolves the board palette from CSS. Called on mount, on resize, and when the
   * system colour scheme flips — never per frame, since getComputedStyle is not cheap.
   */
  readTheme(): void {
    const style = getComputedStyle(this.canvas);
    const read = (name: string, fallback: string) =>
      style.getPropertyValue(name).trim() || fallback;
    this.theme = {
      wall: read('--board-wall', FALLBACK_THEME.wall),
      line: read('--board-line', FALLBACK_THEME.line),
      lineDanger: read('--board-line-danger', FALLBACK_THEME.lineDanger),
      guide: read('--board-guide', FALLBACK_THEME.guide),
      ballShadow: read('--ball-shadow', FALLBACK_THEME.ballShadow),
    };
  }

  /**
   * Sizes the backing store to the element's real pixel size. Called on mount and on
   * resize; without it the canvas is blurry on every retina display.
   */
  resize(cssWidth: number, cssHeight: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.canvas.width = Math.round(cssWidth * dpr);
    this.canvas.height = Math.round(cssHeight * dpr);
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    this.scale = (cssWidth * dpr) / this.engine.worldWidth;
  }

  draw(): void {
    const { ctx } = this;
    const snap = this.engine.snapshot;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.scale(this.scale, this.scale);

    this.drawBoard();
    this.drawFillLine(snap.dangerProgress);

    for (const body of this.engine.bodies) {
      const ball = (body as Ball).ball;
      if (!ball) continue;
      // Ease-out overshoot so merged balls pop into existence instead of appearing.
      const t = ball.spawnFx;
      const pop = t >= 1 ? 1 : 1 + 0.18 * Math.sin(t * Math.PI);
      this.drawBall(body.position.x, body.position.y, ball.radius * pop, ball.rank, body.angle);
    }

    this.drawEffects();

    if (snap.heldRank != null && snap.phase !== 'over') {
      this.drawGuide(snap.heldX, snap.heldRank);
      this.drawBall(snap.heldX, DROP_Y, this.engine.radiusOf(snap.heldRank), snap.heldRank, 0);
    }

    ctx.restore();
  }

  // --- Pieces ---

  /**
   * Only the jar outline. The board's fill lives in CSS on the stage element behind
   * the canvas, so it follows the theme without the renderer compositing colours.
   */
  private drawBoard(): void {
    const { ctx } = this;
    const w = this.engine.worldWidth;
    const h = this.engine.worldHeight;

    ctx.strokeStyle = this.theme.wall;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(2, 0);
    ctx.lineTo(2, h - 2);
    ctx.lineTo(w - 2, h - 2);
    ctx.lineTo(w - 2, 0);
    ctx.stroke();
  }

  private drawFillLine(danger: number): void {
    const { ctx } = this;
    const w = this.engine.worldWidth;
    ctx.save();
    ctx.strokeStyle = danger > 0 ? this.theme.lineDanger : this.theme.line;
    ctx.globalAlpha = danger > 0 ? 0.4 + danger * 0.6 : 1;
    ctx.lineWidth = 3;
    ctx.setLineDash([12, 10]);
    ctx.beginPath();
    ctx.moveTo(0, FILL_LINE_Y);
    ctx.lineTo(w, FILL_LINE_Y);
    ctx.stroke();
    ctx.restore();
  }

  private drawGuide(x: number, rank: number): void {
    const { ctx } = this;
    const r = this.engine.radiusOf(rank);
    ctx.save();
    ctx.strokeStyle = this.theme.guide;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 10]);
    ctx.beginPath();
    ctx.moveTo(x, DROP_Y + r);
    ctx.lineTo(x, this.engine.worldHeight - 4);
    ctx.stroke();
    ctx.restore();
  }

  private drawBall(x: number, y: number, r: number, rank: number, angle: number): void {
    const { ctx } = this;
    const count = this.engine.ballCount;
    const ring = ringWidth(r);
    const inner = r - ring / 2;

    ctx.save();
    ctx.translate(x, y);

    // Contact shadow, so balls sit on each other rather than float.
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, r * 0.12, r * 0.97, 0, Math.PI * 2);
    ctx.fillStyle = this.theme.ballShadow;
    ctx.filter = 'blur(3px)';
    ctx.fill();
    ctx.restore();

    // Photo (or placeholder), clipped to the circle and rotated with the body.
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, inner, 0, Math.PI * 2);
    ctx.clip();

    const image = this.art.imageFor(rank);
    if (image) {
      ctx.save();
      ctx.rotate(angle);
      const d = inner * 2;
      ctx.drawImage(image, -inner, -inner, d, d);
      ctx.restore();
    } else {
      ctx.fillStyle = ballColor(rank, count);
      ctx.fillRect(-inner, -inner, inner * 2, inner * 2);
    }

    // Inner shading: darkens the lower-right so the disc reads as a sphere.
    const shade = ctx.createRadialGradient(
      -inner * 0.35,
      -inner * 0.4,
      inner * 0.15,
      0,
      0,
      inner * 1.15,
    );
    shade.addColorStop(0, 'rgba(255,255,255,0.18)');
    shade.addColorStop(0.55, 'rgba(255,255,255,0)');
    shade.addColorStop(1, 'rgba(60,30,20,0.3)');
    ctx.fillStyle = shade;
    ctx.fillRect(-inner, -inner, inner * 2, inner * 2);
    ctx.restore();

    // The identifying ring.
    ctx.beginPath();
    ctx.arc(0, 0, inner, 0, Math.PI * 2);
    ctx.strokeStyle = ballColor(rank, count);
    ctx.lineWidth = ring;
    ctx.stroke();

    // A darker hairline outside the ring keeps it readable against pale photos.
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.strokeStyle = ballColorDark(rank, count);
    ctx.lineWidth = Math.max(1, ring * 0.28);
    ctx.stroke();

    // Specular highlight, fixed to the world so every ball is lit from the same place.
    ctx.save();
    ctx.rotate(-angle);
    ctx.beginPath();
    ctx.ellipse(-r * 0.36, -r * 0.42, r * 0.26, r * 0.16, -0.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.42)';
    ctx.fill();
    ctx.restore();

    ctx.restore();
  }

  private drawEffects(): void {
    const { ctx } = this;
    const count = this.engine.ballCount;

    for (const fx of this.engine.activeEffects) {
      const t = Math.min(1, Math.max(0, fx.progress));
      const ease = 1 - Math.pow(1 - t, 3);
      ctx.save();
      ctx.globalAlpha = 1 - t;

      // Expanding ring.
      ctx.beginPath();
      ctx.arc(fx.x, fx.y, fx.radius * (0.7 + ease * 0.9), 0, Math.PI * 2);
      ctx.strokeStyle = ballColor(Math.min(fx.rank + 1, count), count);
      ctx.lineWidth = 6 * (1 - t) + 1;
      ctx.stroke();

      // Sparks, bigger and brighter for a top-rank annihilation.
      const sparks = fx.climax ? 14 : 7;
      const reach = fx.radius * (fx.climax ? 2.1 : 1.35);
      ctx.fillStyle = ballColor(fx.rank, count);
      for (let i = 0; i < sparks; i++) {
        const a = (i / sparks) * Math.PI * 2 + fx.rank;
        const d = reach * ease;
        ctx.beginPath();
        ctx.arc(fx.x + Math.cos(a) * d, fx.y + Math.sin(a) * d, 4 * (1 - t) + 1, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }
}
